import React, { useState, useEffect } from 'react';
import { rhuService } from '../../../../services/rhuService';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  useTheme,
  MenuItem,
  Autocomplete,
  InputAdornment,
  Stack
} from '@mui/material';
import {
  RequestQuote,
  AddCircle,
  CheckCircle,
  Visibility,
  Edit,
  Delete,
  Person,
  ListAlt,
  Search,
  Close,
  Inbox,
  Receipt
} from '@mui/icons-material';

const OrderOfPayments = () => {
  const theme = useTheme();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form states for new/edit order
  const [patientName, setPatientName] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [manualAge, setManualAge] = useState('');
  const [address, setAddress] = useState('');
  const [serviceSearch, setServiceSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [searchingServices, setSearchingServices] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    const result = await rhuService.getAllOrders();
    if (result.success) {
      setOrders(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const calculateAge = (birthdate) => {
    if (!birthdate) return '';
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleServiceSearch = async (term) => {
    setServiceSearch(term);
    if (term.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchingServices(true);
    const result = await rhuService.searchServices(term);
    if (result.success) {
      setSearchResults(result.data);
    }
    setSearchingServices(false);
  };

  const addServiceToOrder = (service) => {
    const existing = selectedServices.find(s => s.service_id === service.id);
    if (existing) {
      setError('Service already added');
      return;
    }

    setSelectedServices([...selectedServices, {
      service_id: service.id,
      service_name: service.name,
      unit_price: parseFloat(service.price),
      quantity: 1,
      category: service.subcategory?.category?.name || '',
      subcategory: service.subcategory?.name || ''
    }]);
    setServiceSearch('');
    setSearchResults([]);
  };

  const removeService = (serviceId) => {
    setSelectedServices(selectedServices.filter(s => s.service_id !== serviceId));
  };

  const updateQuantity = (serviceId, quantity) => {
    if (quantity < 1) return;
    setSelectedServices(selectedServices.map(s => 
      s.service_id === serviceId ? { ...s, quantity: parseInt(quantity) } : s
    ));
  };

  const calculateTotal = () => {
    return selectedServices.reduce((sum, item) => {
      return sum + (item.unit_price * item.quantity);
    }, 0);
  };

  const handleCreateOrder = async () => {
    if (!patientName.trim() || !address.trim()) {
      setError('Please fill in patient name and barangay');
      return;
    }

    if (selectedServices.length === 0) {
      setError('Please add at least one service');
      return;
    }

    setLoading(true);

    // Create or get patient
    const patientResult = await rhuService.addPatient({
      name: patientName,
      birthdate: birthdate || null,
      age: manualAge ? parseInt(manualAge) : null,
      address: address
    });

    if (!patientResult.success) {
      setError(patientResult.error);
      setLoading(false);
      return;
    }

    // Create order
    const orderResult = await rhuService.createOrder({
      patient_id: patientResult.data.id,
      items: selectedServices
    });

    if (orderResult.success) {
      setSuccess('Order created successfully!');
      resetForm();
      setShowOrderModal(false);
      loadOrders();
    } else {
      setError(orderResult.error);
    }

    setLoading(false);
  };

  const resetForm = () => {
    setPatientName('');
    setBirthdate('');
    setManualAge('');
    setAddress('');
    setServiceSearch('');
    setSearchResults([]);
    setSelectedServices([]);
    setEditingOrder(null);
    setModalMode('create');
  };

  const handleViewOrder = async (order) => {
    setLoading(true);
    const result = await rhuService.getOrderWithItems(order.id);
    if (result.success) {
      setViewingOrder(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleEditOrder = async (order) => {
    setLoading(true);
    const result = await rhuService.getOrderWithItems(order.id);
    if (result.success) {
      const orderData = result.data;
      setEditingOrder(orderData);
      setPatientName(orderData.patient?.name || '');
      setBirthdate(orderData.patient?.birthdate || '');
      setManualAge(orderData.patient?.age ? orderData.patient.age.toString() : '');
      setAddress(orderData.patient?.address || '');
      setSelectedServices(orderData.items?.map(item => ({
        service_id: item.service_id,
        service_name: item.service_name,
        unit_price: parseFloat(item.unit_price),
        quantity: item.quantity,
        category: item.category || '',
        subcategory: item.subcategory || ''
      })) || []);
      setModalMode('edit');
      setShowOrderModal(true);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleUpdateOrder = async () => {
    if (!patientName.trim() || !address.trim()) {
      setError('Please fill in patient name and barangay');
      return;
    }

    if (selectedServices.length === 0) {
      setError('Please add at least one service');
      return;
    }

    setLoading(true);

    // Update patient
    const patientResult = await rhuService.updatePatient(editingOrder.patient.id, {
      name: patientName,
      birthdate: birthdate || null,
      age: manualAge ? parseInt(manualAge) : null,
      address: address
    });

    if (!patientResult.success) {
      setError(patientResult.error);
      setLoading(false);
      return;
    }

    // Update order
    const orderResult = await rhuService.updateOrder(editingOrder.id, {
      items: selectedServices
    });

    if (orderResult.success) {
      setSuccess('Order updated successfully!');
      resetForm();
      setShowOrderModal(false);
      loadOrders();
    } else {
      setError(orderResult.error);
    }

    setLoading(false);
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;

    setLoading(true);
    const result = await rhuService.deleteOrder(orderId);
    if (result.success) {
      setSuccess('Order deleted successfully');
      loadOrders();
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const filteredOrders = orders.filter(order => 
    order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.patient?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ flexGrow: 1, pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
            color: 'white',
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <RequestQuote sx={{ mr: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                Order of Payments
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Generate and manage patient service orders
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddCircle />}
            onClick={() => {
              resetForm();
              setShowOrderModal(true);
            }}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
          >
            New Order
          </Button>
        </Paper>

        {/* Search Box */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by Order # or Patient Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Orders List */}
        {orders.length === 0 && !loading ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'grey.50',
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2
            }}
          >
            <RequestQuote sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Orders Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Create your first order of payment for a patient
            </Typography>
            <Button
              variant="contained"
              color="error"
              startIcon={<AddCircle />}
              onClick={() => {
                resetForm();
                setShowOrderModal(true);
              }}
            >
              Create First Order
            </Button>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>Patient Name</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      {order.order_number}
                    </TableCell>
                    <TableCell>{order.patient?.name || 'N/A'}</TableCell>
                    <TableCell>
                      {order.patient?.birthdate
                        ? `${calculateAge(order.patient?.birthdate)} years`
                        : (order.patient?.age ? `${order.patient.age} years` : 'N/A')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`₱${parseFloat(order.total_amount).toFixed(2)}`}
                        color="warning"
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.status.toUpperCase()}
                        color={getStatusColor(order.status)}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleViewOrder(order)}
                          title="View details"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleEditOrder(order)}
                          title="Edit order"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteOrder(order.id)}
                          title="Delete order"
                        >
                          <Delete />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* New/Edit Order Modal */}
        <Dialog
          open={showOrderModal}
          onClose={() => setShowOrderModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', display: 'flex', alignItems: 'center' }}>
            <RequestQuote sx={{ mr: 1 }} />
            {modalMode === 'create' ? 'Create New Order of Payment' : 'Edit Order of Payment'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              {/* Patient Information */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" color="error" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <Person sx={{ mr: 1 }} />
                    Patient Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Patient Name *"
                        placeholder="e.g., Juan Dela Cruz"
                        value={patientName}
                        onChange={(e) => setPatientName(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Birthdate"
                        InputLabelProps={{ shrink: true }}
                        value={birthdate}
                        onChange={(e) => {
                          setBirthdate(e.target.value);
                          if (e.target.value) setManualAge('');
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Age"
                        value={birthdate ? calculateAge(birthdate) : manualAge}
                        onChange={(e) => {
                          if (!birthdate) setManualAge(e.target.value);
                        }}
                        InputProps={{
                          readOnly: !!birthdate,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Barangay *"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                      >
                        <MenuItem value="">Select Barangay</MenuItem>
                        <MenuItem value="Bakhawan">Bakhawan</MenuItem>
                        <MenuItem value="Calabasahan">Calabasahan</MenuItem>
                        <MenuItem value="Dalajican">Dalajican</MenuItem>
                        <MenuItem value="Masadya">Masadya</MenuItem>
                        <MenuItem value="Masudsud">Masudsud</MenuItem>
                        <MenuItem value="Poblacion">Poblacion</MenuItem>
                        <MenuItem value="San Pedro">San Pedro</MenuItem>
                        <MenuItem value="San Vicente">San Vicente</MenuItem>
                        <MenuItem value="Sampong">Sampong</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Services Section */}
              <Grid item xs={12}>
                <Typography variant="h6" color="error" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListAlt sx={{ mr: 1 }} />
                  Services
                </Typography>
                
                {/* Service Search */}
                <Box sx={{ mb: 3 }}>
                  <Autocomplete
                    fullWidth
                    freeSolo
                    options={searchResults}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Services"
                        placeholder="Search by service name or category..."
                        onChange={(e) => handleServiceSearch(e.target.value)}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              {searchingServices ? <CircularProgress size={20} /> : <Search />}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} onClick={() => addServiceToOrder(option)}>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">{option.name}</Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {option.subcategory?.category?.name} → {option.subcategory?.name}
                            </Typography>
                            <Chip label={`₱${parseFloat(option.price).toFixed(2)}`} size="small" color="primary" variant="outlined" />
                          </Box>
                        </Box>
                      </li>
                    )}
                  />
                </Box>

                {/* Selected Services */}
                {selectedServices.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                          <TableCell>Service</TableCell>
                          <TableCell align="center" width={100}>Qty</TableCell>
                          <TableCell align="right">Price</TableCell>
                          <TableCell align="right">Subtotal</TableCell>
                          <TableCell width={60}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedServices.map((item) => (
                          <TableRow key={item.service_id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">{item.service_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.category} → {item.subcategory}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={item.quantity}
                                onChange={(e) => updateQuantity(item.service_id, e.target.value)}
                                InputProps={{ inputProps: { min: 1 } }}
                                sx={{ width: 70 }}
                              />
                            </TableCell>
                            <TableCell align="right">₱{item.unit_price.toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              ₱{(item.unit_price * item.quantity).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeService(item.service_id)}
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: 'error.50' }}>
                          <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>TOTAL AMOUNT:</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main', fontSize: '1.1rem' }}>
                            ₱{calculateTotal().toFixed(2)}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      bgcolor: 'grey.50',
                      borderStyle: 'dashed'
                    }}
                  >
                    <Inbox sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">
                      No services added yet. Search and add services above.
                    </Typography>
                  </Paper>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setShowOrderModal(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={modalMode === 'create' ? handleCreateOrder : handleUpdateOrder}
              disabled={loading || selectedServices.length === 0}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
            >
              {modalMode === 'create' ? 'Create Order' : 'Update Order'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Order Dialog */}
        <Dialog
          open={Boolean(viewingOrder)}
          onClose={() => setViewingOrder(null)}
          maxWidth="md"
          fullWidth
        >
          {viewingOrder && (
            <>
              <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center' }}>
                <Receipt sx={{ mr: 1 }} />
                Order Details - {viewingOrder.order_number}
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <Person sx={{ mr: 1 }} />
                        Patient Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Name</Typography>
                          <Typography variant="body1" fontWeight="medium">{viewingOrder.patient?.name}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Age</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {viewingOrder.patient?.birthdate
                              ? `${calculateAge(viewingOrder.patient?.birthdate)} years`
                              : (viewingOrder.patient?.age ? `${viewingOrder.patient.age} years` : 'N/A')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Address</Typography>
                          <Typography variant="body1" fontWeight="medium">{viewingOrder.patient?.address}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <ListAlt sx={{ mr: 1 }} />
                      Services
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead sx={{ bgcolor: 'grey.100' }}>
                          <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell align="center">Qty</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Subtotal</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {viewingOrder.items?.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">{item.service_name}</Typography>
                                {item.subcategory && (
                                  <Typography variant="caption" color="text.secondary">
                                    {item.category && `${item.category} → `}{item.subcategory}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">{item.quantity}</TableCell>
                              <TableCell align="right">₱{parseFloat(item.unit_price).toFixed(2)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                ₱{parseFloat(item.subtotal).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: 'primary.50' }}>
                            <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>TOTAL:</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.1rem' }}>
                              ₱{parseFloat(viewingOrder.total_amount).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  {viewingOrder.status === 'paid' && (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50', borderColor: 'success.main' }}>
                        <Typography variant="h6" color="success.dark" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircle sx={{ mr: 1 }} />
                          Payment Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">OR Number</Typography>
                            <Typography variant="body1" fontWeight="bold" color="success.dark">
                              {viewingOrder.or_number || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Payment Date</Typography>
                            <Typography variant="body1" fontWeight="bold" color="success.dark">
                              {viewingOrder.paid_at ? new Date(viewingOrder.paid_at).toLocaleString() : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">Processed By</Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {viewingOrder.processed_by || 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  )}

                  <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    <Chip
                      label={`Status: ${viewingOrder.status.toUpperCase()}`}
                      color={getStatusColor(viewingOrder.status)}
                      sx={{ fontWeight: 'bold', px: 2 }}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setViewingOrder(null)}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Feedback Snackbar */}
        <Snackbar
          open={Boolean(error || success)}
          autoHideDuration={3000}
          onClose={() => { setError(''); setSuccess(''); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => { setError(''); setSuccess(''); }}
            severity={error ? 'error' : 'success'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {error || success}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default OrderOfPayments;
